import { apiFetch } from '@/lib/api';

export interface PlacePhoto {
  photo_reference: string;
  url: string;
  width: number;
  height: number;
  html_attributions: string[];
}

export interface PlacePhotosResponse {
  place_id: string;
  photos: PlacePhoto[];
}

export class PlacesService {
  /**
   * Get photos for a Google Place ID
   * @param placeId - Google Place ID
   * @param maxWidth - Maximum photo width (default: 800)
   * @param shareLinkToken - Optional share link token for public access
   * @param sessionId - Optional session ID for public access
   */
  async getPlacePhotos(
    placeId: string, 
    maxWidth: number = 800,
    shareLinkToken?: string,
    sessionId?: string
  ): Promise<PlacePhotosResponse> {
    let url = `/api/places/photos?place_id=${encodeURIComponent(placeId)}&maxwidth=${maxWidth}`;
    if (shareLinkToken) {
      url += `&share_link_token=${encodeURIComponent(shareLinkToken)}`;
    }
    
    const headers: Record<string, string> = {};
    if (sessionId) {
      headers['X-Session-Id'] = sessionId;
    }
    
    const response = await apiFetch(url, {
      headers,
      skipRedirect: true, // Don't redirect on 401 - let the error bubble up
      skipRefresh: true, // Don't try to refresh token for public share links
      credentials: 'include', // Include auth cookies (for platform users)
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to fetch place photos' }));
      throw new Error(error.message || 'Failed to fetch place photos');
    }
    
    const data = await response.json();
    return data.data;
  }

  /**
   * Update venue's Google Place ID
   */
  async updateVenuePlaceId(venueId: string, placeId: string | null): Promise<void> {
    const response = await apiFetch(`/api/venues/${venueId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ place_id: placeId }),
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to update place ID' }));
      throw new Error(error.message || 'Failed to update place ID');
    }
  }
}

